import { EditOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Divider, Flex, Select } from "antd";
import { useMemo, useState } from "react";
import { AssigneeAvatarGroup } from "../../common/components/AssigneeAvatarGroup";
import type { UserLimitedList } from "../generated-axios-client";
import {
  assigneeOptionRender,
  assigneeSelectOptions,
  searchHandler,
} from "./create-forms/MultiAssigneeSelectorFormItem";
import styles from "./EditableAssignees.module.css";

// A fixed tag count rather than "responsive": responsive measures the select's width,
// which in an auto-width table column depends on the tags shown, so a single long
// name flips between the full tag and "+1" forever.
const VISIBLE_TAG_COUNT = 1;
const TAG_TEXT_LENGTH = 14;

type EditableAssigneesProps = {
  intl: IntlShape;
  assignees: UserLimitedList[];
  technicians: UserLimitedList[];
  onChange?: (assigneeIds: number[]) => void;
};

const haveSameIds = (first: number[], second: number[]) =>
  first.length === second.length && first.every((id) => second.includes(id));

export const EditableAssignees = (props: EditableAssigneesProps) => {
  const { intl, assignees, technicians, onChange } = props;

  const [editing, setEditing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const options = useMemo(() => assigneeSelectOptions(technicians), [technicians]);
  const currentIds = assignees.map((user) => user.id);
  const selectionChanged = !haveSameIds(selectedIds, currentIds);

  const startEditing = () => {
    setSelectedIds(currentIds);
    setEditing(true);
  };

  const cancelEditing = () => setEditing(false);

  const saveEditing = () => {
    setEditing(false);
    if (selectionChanged) {
      onChange?.(selectedIds);
    }
  };

  if (editing) {
    return (
      <Select
        className={styles.select}
        mode="multiple"
        autoFocus
        open
        showSearch
        maxTagCount={VISIBLE_TAG_COUNT}
        maxTagTextLength={TAG_TEXT_LENGTH}
        popupMatchSelectWidth={false}
        options={options}
        filterOption={searchHandler}
        optionRender={assigneeOptionRender}
        value={selectedIds}
        onChange={setSelectedIds}
        onOpenChange={(open) => {
          if (!open) cancelEditing();
        }}
        popupRender={(menu) => (
          <>
            {menu}
            <Divider size="small" />
            {/* Keeps focus in the select, so clicking a button does not close the popup first */}
            <Flex gap="small" justify="flex-end" onMouseDown={(e) => e.preventDefault()}>
              <Button size="small" onClick={cancelEditing}>
                {intl.formatMessage({ id: "cancel" })}
              </Button>
              <Button
                size="small"
                type="primary"
                disabled={!selectionChanged}
                onClick={saveEditing}
              >
                {intl.formatMessage({ id: "save" })}
              </Button>
            </Flex>
          </>
        )}
      />
    );
  }

  return (
    <Flex align="center" gap={4}>
      <AssigneeAvatarGroup assignees={assignees} />
      {onChange && (
        <Button
          type="text"
          size="small"
          icon={<EditOutlined />}
          aria-label="Edit assignees"
          onClick={startEditing}
        />
      )}
    </Flex>
  );
};
